<template>
  <div 
    class="shelf-container"
    :style="shelfContainerStyle"
    @dragover="allowDrop"
    @drop="onDrop"
  >
    <!-- Shelf Base -->
    <div class="shelf-base" :style="shelfBaseStyle">
      <!-- Shelf Controls - appear only on hover -->
      <div class="shelf-controls">
        <v-btn 
          x-small 
          icon 
          color="red" 
          @click="deleteShelf"
          class="delete-btn"
        >
          <v-icon x-small>mdi-delete</v-icon>
        </v-btn>
        <v-btn 
          x-small 
          icon 
          color="blue" 
          @click="showConfigDialog = true"
          class="config-btn"
        >
          <v-icon x-small>mdi-cog</v-icon>
        </v-btn>
        
        <!-- Drag Handle for Moving Shelf -->
        <div 
          class="shelf-drag-handle"
          @mousedown="startDragShelf"
        >
          <v-icon x-small>mdi-drag</v-icon>
        </div>
      </div>

      <!-- Shelf Label -->
      <div class="shelf-label">
        <div class="shelf-dimensions">
          {{ roundToHalfCm(shelf.length) }}cm x {{ roundToHalfCm(shelf.depth) }}cm
        </div>
        <div class="shelf-position">
          Alt: {{ roundToHalfCm(shelf.verticalPosition) }}cm
        </div>
      </div>

      <!-- Products on Shelf -->
      <div class="shelf-products">
        <div 
          v-for="(product, index) in shelf.products"
          :key="`${product.id}-${index}`"
          class="shelf-product"
          :style="getProductStyle(product, index)"
        >
          <!-- Product Visual -->
          <div class="product-visual" :style="getProductVisualStyle(product)">
            <img 
              :src="`/images/catalog/lower/${product.cod_articolo.replace('/', '_')}.jpg`"
              :alt="product.description"
              class="shelf-product-image"
              @error="$event.target.style.display='none'"
            />
            
            <!-- Product Info Tooltip - appears on hover -->
            <v-tooltip 
              top
              max-width="350"
              :open-delay="200"
              content-class="product-info-tooltip"
            >
              <template v-slot:activator="{ on, attrs }">
                <div 
                  class="product-hover-area"
                  v-bind="attrs"
                  v-on="on"
                >
                  <!-- This invisible area covers the entire product for hover detection -->
                </div>
              </template>
              <div class="product-info-popup">
                <!-- Product Image -->
                <div class="tooltip-image-section">
                  <img 
                    :src="`/images/catalog/lower/${product.cod_articolo.replace('/', '_')}.jpg`"
                    :alt="product.description"
                    class="tooltip-product-image"
                    @error="$event.target.style.display='none'"
                  />
                </div>
                
                <!-- Product Details -->
                <div class="tooltip-info-section">
                  <div class="product-title">{{ product.description }}</div>
                  
                  <div class="product-details">
                    <div class="detail-row">
                      <v-icon small color="primary">mdi-barcode</v-icon>
                      <span class="detail-label">Codice:</span>
                      <span class="detail-value">{{ product.cod_articolo }}</span>
                    </div>
                    
                    <div class="detail-row">
                      <v-icon small color="primary">mdi-ruler</v-icon>
                      <span class="detail-label">Dimensioni:</span>
                      <span class="detail-value">
                        L{{ product.larghezza ? roundToHalfCm(product.larghezza) : '?' }} × 
                        P{{ product.profondita ? roundToHalfCm(product.profondita) : '?' }} × 
                        A{{ product.altezza ? roundToHalfCm(product.altezza) : '?' }} cm
                      </span>
                    </div>
                    
                    <div class="detail-row">
                      <v-icon small color="primary">mdi-package-variant</v-icon>
                      <span class="detail-label">Stock:</span>
                      <span class="detail-value" :class="{ 'stock-low': (product.quantita_sede || 0) <= 0 }">
                        {{ product.quantita_sede || 0 }} pz
                      </span>
                    </div>
                    
                    <div v-if="product.prezzo_vendita" class="detail-row">
                      <v-icon small color="primary">mdi-currency-eur</v-icon>
                      <span class="detail-label">Prezzo:</span>
                      <span class="detail-value">€ {{ product.prezzo_vendita }}</span>
                    </div>
                    
                    <div v-if="product.barcode" class="detail-row">
                      <v-icon small color="primary">mdi-barcode-scan</v-icon>
                      <span class="detail-label">Barcode:</span>
                      <span class="detail-value barcode">{{ product.barcode }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </v-tooltip>

            <!-- Remove Product Button -->
            <v-btn
              x-small
              icon
              color="red"
              class="remove-product-btn"
              @click.stop="removeProduct(index)"
              elevation="2"
            >
              <v-icon x-small color="white">mdi-close-circle</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Drop Zone Indicator -->
      <div 
        v-if="isDragging && isValidDrop" 
        class="drop-zone"
      >
        <v-icon color="primary">mdi-plus</v-icon>
        <div class="text-caption">Rilascia qui</div>
      </div>

      <!-- Empty Shelf Message -->
      <div 
        v-if="shelf.products.length === 0 && !isDragging" 
        class="empty-shelf-message"
      >
        <v-icon color="grey">mdi-package-variant-closed</v-icon>
        <div class="text-caption grey--text">Vuoto</div>
      </div>
    </div>

    <!-- Configuration Dialog -->
    <v-dialog v-model="showConfigDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left>mdi-cog</v-icon>
          Configura Scaffale
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="configForm.length"
                label="Lunghezza (cm)"
                type="number"
                min="20"
                max="200"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="configForm.depth"
                label="Profondità (cm)"
                type="number"
                min="10"
                max="60"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="configForm.verticalPosition"
                label="Altezza (cm)"
                type="number"
                min="0"
                max="300"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="configForm.horizontalPosition"
                label="Posizione X (cm)"
                type="number"
                min="0"
                max="400"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelConfig">Annulla</v-btn>
          <v-btn color="primary" @click="applyConfig">Salva</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ProductImage from '../ProductImage.vue'

export default {
  name: 'PlanogramShelf',
  components: {
    ProductImage
  },
  props: {
    shelf: {
      type: Object,
      required: true
    },
    isDragging: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showConfigDialog: false,
      configForm: {
        length: 0,
        depth: 0,
        verticalPosition: 0,
        horizontalPosition: 0
      },
      isValidDrop: false,
      isDraggingShelf: false,
      dragStartPos: { x: 0, y: 0 }
    }
  },
  computed: {
    shelfContainerStyle() {
      return {
        position: 'absolute',
        left: `${this.shelf.horizontalPosition}px`,
        bottom: `${this.shelf.verticalPosition}px`,
        zIndex: this.isDraggingShelf ? 1000 : 10
      }
    },

    shelfBaseStyle() {
      return {
        width: `${this.shelf.length}px`,
        height: `${this.shelf.depth}px`,
        backgroundColor: this.getShelfColor(),
        border: `2px solid ${this.getBorderColor()}`,
        borderRadius: '4px',
        position: 'relative',
        minHeight: '30px',
        boxShadow: this.isDraggingShelf 
          ? '0 8px 24px rgba(0,0,0,0.3)' 
          : '0 2px 8px rgba(0,0,0,0.1)'
      }
    }
  },
  watch: {
    shelf: {
      handler(newShelf) {
        this.updateConfigForm()
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // Helper function to round to nearest 0.5cm
    roundToHalfCm(value) {
      if (!value || isNaN(value)) return 0
      return Math.round(value * 2) / 2
    },

    // Shelf appearance
    getShelfColor() {
      if (this.isDragging && this.isValidDrop) {
        return '#e3f2fd'
      }
      if (this.shelf.products.length === 0) {
        return '#f5f5f5'
      }
      return '#fff'
    },

    getBorderColor() {
      if (this.isDragging && this.isValidDrop) {
        return '#1976d2'
      }
      if (this.shelf.products.length === 0) {
        return '#ddd'
      }
      return '#1976d2'
    },

    // Product positioning
    getProductStyle(product, index) {
      const spacing = 5 // Aumentiamo lo spazio tra i prodotti
      let leftPosition = spacing
      
      // Calculate position based on previous products with their actual visual size
      for (let i = 0; i < index; i++) {
        const prevProduct = this.shelf.products[i]
        const prevBaseWidth = prevProduct.larghezza || 40
        const prevVisualWidth = Math.max(prevBaseWidth * 1.5, 50)
        leftPosition += prevVisualWidth + spacing
      }

      return {
        position: 'absolute',
        left: `${leftPosition}px`,
        top: '3px',
        zIndex: 20
      }
    },

    getProductVisualStyle(product) {
      // Ingrandiamo significativamente i prodotti per renderli molto più visibili
      const baseWidth = product.larghezza || 40
      const baseHeight = product.altezza || 40
      
      // Scala i prodotti con un fattore minimo più alto
      const width = Math.max(baseWidth * 1.5, 50)  // Minimo 50px, scala 1.5x
      const height = Math.max(baseHeight * 1.2, 35) // Minimo 35px, scala 1.2x
      
      return {
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: '#ffffff',
        border: '3px solid #1976d2',
        borderRadius: '6px',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }
    },

    // Drag and drop for products
    allowDrop(event) {
      event.preventDefault()
      if (this.isDragging) {
        this.isValidDrop = true
      }
    },

    onDrop(event) {
      event.preventDefault()
      event.stopPropagation()
      
      if (!this.isDragging) return

      try {
        const productData = JSON.parse(event.dataTransfer.getData('text/plain'))
        this.$emit('drop-product', this.shelf.id, productData)
      } catch (error) {
        console.error('Error parsing dropped product data:', error)
      }
      
      this.isValidDrop = false
    },

    // Product management
    removeProduct(index) {
      const updatedShelf = { ...this.shelf }
      updatedShelf.products = [...updatedShelf.products]
      updatedShelf.products.splice(index, 1)
      this.$emit('update-shelf', updatedShelf)
    },

    // Shelf management
    deleteShelf() {
      if (this.shelf.products.length > 0) {
        this.$confirm('Questo scaffale contiene dei prodotti. Sei sicuro di volerlo eliminare?', {
          title: 'Conferma Eliminazione',
          buttonTrueText: 'Elimina',
          buttonFalseText: 'Annulla'
        }).then((result) => {
          if (result) {
            this.$emit('delete-shelf', this.shelf.id)
          }
        })
      } else {
        this.$emit('delete-shelf', this.shelf.id)
      }
    },

    // Shelf configuration
    updateConfigForm() {
      this.configForm = {
        length: this.shelf.length,
        depth: this.shelf.depth,
        verticalPosition: this.shelf.verticalPosition,
        horizontalPosition: this.shelf.horizontalPosition
      }
    },

    cancelConfig() {
      this.showConfigDialog = false
      this.updateConfigForm()
    },

    applyConfig() {
      const updatedShelf = {
        ...this.shelf,
        length: parseInt(this.configForm.length),
        depth: parseInt(this.configForm.depth),
        verticalPosition: parseInt(this.configForm.verticalPosition),
        horizontalPosition: parseInt(this.configForm.horizontalPosition)
      }
      
      this.$emit('update-shelf', updatedShelf)
      this.showConfigDialog = false
    },

    // Shelf dragging
    startDragShelf(event) {
      this.isDraggingShelf = true
      this.dragStartPos = {
        x: event.clientX - this.shelf.horizontalPosition,
        y: event.clientY + this.shelf.verticalPosition // Fix: Add instead of subtract for correct Y calculation
      }

      const onMouseMove = (e) => {
        if (this.isDraggingShelf) {
          const newX = Math.max(0, Math.min(e.clientX - this.dragStartPos.x, 400)) // Limit horizontal movement
          const newY = Math.max(0, Math.min(this.dragStartPos.y - e.clientY, 300)) // Fix: Invert Y calculation for proper movement
          
          const updatedShelf = {
            ...this.shelf,
            horizontalPosition: newX,
            verticalPosition: newY
          }
          
          this.$emit('update-shelf', updatedShelf)
        }
      }

      const onMouseUp = () => {
        this.isDraggingShelf = false
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }
  }
}
</script>

<style scoped>
.shelf-container {
  transition: all 0.2s ease;
}

.shelf-base {
  position: relative;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  transition: all 0.3s ease;
}

.shelf-base:hover {
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

/* Shelf Controls - Hidden by default, visible on hover */
.shelf-controls {
  position: absolute;
  top: -32px;
  right: 0;
  display: flex;
  gap: 6px;
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  transform: translateY(-5px);
}

.shelf-container:hover .shelf-controls {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.delete-btn, .config-btn {
  background: white !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.shelf-drag-handle {
  background: #1976d2;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.shelf-drag-handle:active {
  cursor: grabbing;
}

/* Shelf Label */
.shelf-label {
  position: absolute;
  top: -30px;
  left: 0;
  font-size: 10px;
  color: #666;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid #ddd;
}

.shelf-dimensions {
  font-weight: 600;
  color: #1976d2;
}

/* Products */
.shelf-products {
  position: relative;
  height: 100%;
  overflow: visible;
}

.shelf-product {
  transition: all 0.2s ease;
}

.product-visual {
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-visual:hover {
  transform: scale(1.05);
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.shelf-product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 3px;
}


.remove-product-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #f44336 !important;
  color: white !important;
  border: 2px solid white !important;
  box-shadow: 0 3px 6px rgba(244, 67, 54, 0.4);
  opacity: 0.3;
  transition: all 0.2s ease;
  z-index: 100;
  min-width: 22px !important;
  width: 22px !important;
  height: 22px !important;
}

.remove-product-btn:hover {
  background: #d32f2f !important;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(244, 67, 54, 0.6);
}

.product-visual:hover .remove-product-btn {
  opacity: 1;
}

/* Product Hover Area - invisible overlay for hover detection */
.product-hover-area {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  cursor: pointer;
}

/* Product Info Popup - with image and text */
.product-info-popup {
  display: flex;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  border: 2px solid #1976d2;
  min-width: 320px;
  max-width: 350px;
}

.tooltip-image-section {
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tooltip-product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0;
}

.tooltip-info-section {
  flex: 1;
  padding: 12px;
}

.product-title {
  font-size: 14px;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 10px;
  line-height: 1.3;
  max-height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 1.2;
}

.detail-label {
  font-weight: 500;
  color: #424242;
  min-width: 50px;
}

.detail-value {
  color: #212121;
  font-weight: 400;
}

.detail-value.stock-low {
  color: #f44336;
  font-weight: 600;
}

.detail-value.barcode {
  font-family: 'Courier New', monospace;
  font-size: 10px;
  background: #f0f0f0;
  padding: 2px 4px;
  border-radius: 3px;
}

/* Global tooltip styling override */
:global(.product-info-tooltip) {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

/* Drop Zone */
.drop-zone {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(25, 118, 210, 0.1);
  border: 2px dashed #1976d2;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  color: #1976d2;
  min-width: 80px;
}

/* Empty Shelf */
.empty-shelf-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  opacity: 0.6;
  white-space: nowrap;
  max-width: calc(100% - 16px);
  overflow: hidden;
  font-size: 10px;
  padding: 3px 6px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 3px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .shelf-controls {
    top: -25px;
  }
  
  .shelf-label {
    top: -25px;
    font-size: 9px;
  }
  
  .product-visual {
    min-width: 15px;
    min-height: 15px;
  }
  
  .empty-shelf-message {
    font-size: 8px;
    padding: 2px 4px;
    max-width: calc(100% - 8px);
  }
  
  .empty-shelf-message .v-icon {
    font-size: 12px !important;
  }
}
</style>